import React from 'react';

import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface SummaryCardProps {
  testId?: string;
  icon: React.ReactNode;
  value: string | number;
  label: string;
  description: string;
  color: 'blue' | 'green' | 'yellow' | 'red' | 'grey';
  isSelected?: boolean;
  isLoading?: boolean;
  onClick: () => void;
}

const getSelectedBorderClass = (color: string) => {
  if (color === 'blue') return 'vai-border-blue-600';
  if (color === 'green') return 'vai-border-green-600';
  if (color === 'yellow') return 'vai-border-amber-500';
  if (color === 'red') return 'vai-border-red-600';
  if (color === 'grey') return 'vai-border-gray-500';
  return 'vai-border-blue-600';
};

const getValueColorClass = (color: string) => {
  if (color === 'blue') return 'vai-text-blue-600';
  if (color === 'green') return 'vai-text-green-600';
  if (color === 'yellow') return 'vai-text-amber-600';
  if (color === 'red') return 'vai-text-red-600';
  if (color === 'grey') return 'vai-text-gray-600';
  return 'vai-text-blue-600';
};

const getLabelColorClass = (color: string, isSelected: boolean) => {
  if (!isSelected) return 'vai-text-zinc-500';
  if (color === 'blue') return 'vai-text-blue-600';
  if (color === 'green') return 'vai-text-green-600';
  if (color === 'yellow') return 'vai-text-amber-600';
  if (color === 'red') return 'vai-text-red-600';
  if (color === 'grey') return 'vai-text-gray-600';
  return 'vai-text-blue-600';
};

const getCardClasses = () => {
  return 'vai-flex vai-flex-col vai-gap-2 vai-rounded-xl vai-w-full vai-cursor-pointer vai-border vai-h-full transition-all duration-200 vai-shadow-sm hover:vai-shadow-md';
};

export const SummaryCard = React.memo(
  ({
    testId = 'summary-card',
    icon,
    value,
    label,
    description,
    color,
    isSelected,
    isLoading,
    onClick,
  }: SummaryCardProps) => {
    return (
      <div
        // Includes 1px default border
        className={getCardClasses()}
        data-testid={testId}
      >
        <div
          // Props to make card clickable and accessible
          role="button"
          tabIndex={0}
          onClick={onClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onClick();
            }
          }}
          // Adds 2px colored border on top when selected
          className={`vai-flex vai-flex-col vai-rounded-xl vai-px-8 vai-py-6 vai-bg-white vai-border-2 ${
            isSelected
              ? getSelectedBorderClass(color)
              : 'vai-border-transparent'
          }`}
          data-testid={`${testId}-button`}
        >
          <div className="vai-flex vai-justify-between">
            <div className="vai-flex vai-flex-col vai-gap-1">
              <span
                className={`vai-text-base vai-font-medium vai-uppercase vai-h-6 vai-tracking-[0.03em] ${getLabelColorClass(
                  color,
                  isSelected || false,
                )}`}
                data-testid={`${testId}-label`}
              >
                {label}
              </span>
              <div className="vai-flex vai-items-center vai-gap-4 vai-py-1">
                <span
                  className={`vai-text-6xl vai-font-bold ${getValueColorClass(
                    color,
                  )}`}
                  data-testid={`${testId}-value`}
                >
                  {value}
                </span>
                <span
                  className="vai-text-base vai-text-zinc-900 vai-max-w-52"
                  data-testid={`${testId}-description`}
                >
                  {description}
                </span>
              </div>
            </div>
            <div
              className="vai-w-6 vai-h-6 vai-flex vai-items-center vai-justify-center"
              data-testid={`${testId}-icon`}
            >
              {isLoading ? (
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="vai-text-blue-500 vai-animate-spin"
                />
              ) : (
                icon
              )}
            </div>
          </div>
        </div>
      </div>
    );
  },
);
